import { collectionsService } from '@services/firebase/firestore/functionFactory';
import { InitialDataService, WithRequired } from '@services/loadInitialData';
import { PermissionsService } from '@services/permissions';
import { surveyRenderDataFactory } from '@services/surveyRenderData';
import { appUserStore } from '@stores/bootstrap-stores/user';
import { hasItems } from '@utils/vanillaJs';
import { Campaign, SurveyResponse } from '@vincerosoft/assessfluence-types';
import { QueryConstraint, where } from 'firebase/firestore';

const whereEntity = (entityId: string) => where('entity_id', '==', entityId);
const notPartial = where('partial', '==', false);
const whereMember = (memberId: string) => where('respondent.member_id', '==', memberId);

export function constructQueriesByAL() {
  function fetchUserResponseQuery<T extends 'firestore' | 'api'>(
    queryEngine: T,
    cb: (queryScope: 'all' | 'group' | 'own') => void
  ) {
    const member = appUserStore.getState().currentMember;

    // Guard against null member
    if (!member) {
      throw new Error('Current member is not initialized');
    }

    let query: QueryConstraint[] | Record<string, unknown>;
    let batchQuery: [string, 'in' | 'array-contains-any', string[]] | null = null;

    if (PermissionsService.canReadAll('response')) {
      cb('all');
      query =
        queryEngine === 'firestore'
          ? ([
              where('entity_id', '==', member.entity_id),
              where('form.intent', '!=', 'content')
            ] as QueryConstraint[])
          : {
              entity_id: member.entity_id,
              'form.intent': { $ne: 'content' }
            };
    } else if (hasItems(member.user_group_ids) && PermissionsService.canReadGroup('response')) {
      cb('group');
      query =
        queryEngine === 'firestore'
          ? ([where('form.intent', '!=', 'content')] as QueryConstraint[])
          : {
              'form.intent': { $ne: 'content' },
              user_group_id: { $in: member.user_group_ids }
            };
      batchQuery =
        queryEngine === 'firestore' ? ['user_group_id', 'in', member.user_group_ids] : null;
    } else {
      cb('own');
      query =
        queryEngine === 'firestore'
          ? ([where('respondent.member_id', '==', member.id)] as QueryConstraint[])
          : { 'respondent.member_id': member.id };
    }

    return { query, batchQuery };
  }

  return { fetchUserResponseQuery };
}

function responsesFactory() {
  const getResponsesCountByMember = (entityId: string, memberId: string) => {
    const query = [whereEntity(entityId)];
    query.push(whereMember(memberId), notPartial);
    return collectionsService.responses.getCount(query);
  };

  const getRecentResponsesByMember = async (entityId: string, memberId: string, fromData: Date) => {
    const query = [whereEntity(entityId)];
    query.push(whereMember(memberId), notPartial, where('created_at', '>=', fromData));
    return collectionsService.responses.getDocs(query);
  };

  const getResponsesAssociatedData = async (responses: SurveyResponse[]) => {
    const linkedCampaignIds = responses
      .map((res) => res.campaign?.id)
      .filter((id): id is string => !!id);
    return surveyRenderDataFactory(responses, 'response', linkedCampaignIds);
  };

  async function loadResponsesStore(onProgress: (responses: SurveyResponse[]) => void) {
    const member = appUserStore.getState().currentMember;

    type RequiredCreatedAt = WithRequired<SurveyResponse, 'created_at'>;
    let compoundedResponses: RequiredCreatedAt[] = [];

    const loadResponsesBatch = async (
      query: QueryConstraint[],
      batchQuery?: [string, 'in' | 'array-contains-any', string[]]
    ) =>
      await InitialDataService.load<SurveyResponse>(
        'responses',
        query,
        {},
        (responses) => {
          compoundedResponses = [...responses, ...compoundedResponses];

          const uniqResponses =
            InitialDataService.uniqueItemsSortedByCreatedAt<RequiredCreatedAt>()(
              compoundedResponses
            );
          onProgress(uniqResponses);
        },
        batchQuery
      );

    let queryScope: 'all' | 'group' | 'own' | null = null;

    const { query, batchQuery } = constructQueriesByAL().fetchUserResponseQuery(
      'firestore',
      (qS) => (queryScope = qS)
    );

    // Start both queries if they exist, but only mark initialization complete after both finish

    if (queryScope === 'group') {
      await loadResponsesBatch(
        query as QueryConstraint[],
        batchQuery as [string, 'in' | 'array-contains-any', string[]]
      );
      /* const ownResponses = await collectionsService.responses.getDocs(
        where('respondent.member_id', '==', member!.id)
      ); */
      //  compoundedResponses.push(...(ownResponses as RequiredCreatedAt[]));
    } else {
      await loadResponsesBatch(query as QueryConstraint[]);
    }

    const collResponses = (await collectionsService.responses.getDocs(
      where('collaborator_ids', 'array-contains', member!.user_id)
    )) as RequiredCreatedAt[];
    compoundedResponses.push(...(collResponses as RequiredCreatedAt[]));
    return compoundedResponses;
  }

  const getResponsesCountByEntityId = (entityId: string) =>
    collectionsService.responses.getCount([whereEntity(entityId), notPartial]);

  const getResponseByInvitationId = async (invitationId: string) => {
    const responses = await collectionsService.responses.getDocs([
      where('invitation.id', '==', invitationId)
    ]);

    return hasItems(responses) ? (responses[0] as SurveyResponse) : null;
  };

  const getResponsesByInvitationEnvId = async (invitationEnvId: string) => {
    return collectionsService.responses.getDocs([
      where('invitation.envelope_id', '==', invitationEnvId)
    ]);
  };

  const getResponsesByInvitationEnvIds = async (
    invitationEnvIds: string[],
    setResponses?: (responses: SurveyResponse[]) => void
  ) => {
    const responses = await collectionsService.responses.getDocsInBatches(
      ['invitation.envelope_id', 'in', invitationEnvIds],
      undefined,
      setResponses
    );
    return responses.flat() as SurveyResponse[];
  };

  const getRelayResponse = (formId: string, campaign: Pick<Campaign, 'id' | 'entity_id'>) => {
    const queryConstraints = [
      where('form.id', '==', formId),
      where('campaign.id', '==', campaign.id),
      where('entity_id', '==', campaign.entity_id)
    ];

    return collectionsService.responses.getDocs(queryConstraints);
  };

  const getResponseByUidAndCampaignId = async (uid: string, campaignId: string) => {
    const responses = await collectionsService.responses.getDocs([
      where('respondent.uid', '==', uid),
      where('campaign.id', '==', campaignId)
    ]);
    if (hasItems(responses)) {
      return responses[0] as SurveyResponse;
    }
    return null;
  };

  return {
    getResponsesAssociatedData,
    getResponsesCountByMember,
    getRecentResponsesByMember,
    loadResponsesStore,
    getResponsesCountByEntityId,
    getResponseByInvitationId,
    getResponsesByInvitationEnvId,
    getResponsesByInvitationEnvIds,
    getRelayResponse,
    getResponseByUidAndCampaignId
  };
}

export const responsesService = responsesFactory();
